import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { 
  ChevronLeft, Send, AlertTriangle, CheckCircle, 
  Users, Mail, Eye, Monitor, Smartphone, Shield,
  Clock, Zap, Timer, BarChart3
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============== DEFAULT EMAIL STYLES ==============
const defaultEmailStyles = {
  useWncHeader: true,
  showLogo: true,
  headerBgColor: '#001428',
  headerAccentColor: '#d4a853',
  headerTextColor: '#ffffff',
  headerOverline: 'World Nuclear Congress',
  headerTitle: '',
  headerSubtitle: '',
  bodyBgColor: '#ffffff',
  bodyTextColor: '#333333',
  bodyFontFamily: 'Arial, sans-serif',
  bodyFontSize: '16px',
  bodyLineHeight: '1.6',
  buttonBgColor: '#d4a853',
  buttonTextColor: '#001428',
  buttonBorderRadius: '4px',
  footerBgColor: '#f8f9fa',
  footerTextColor: '#666666',
  footerBorderColor: '#e5e7eb',
};

// ============== WNC PREMIUM HEADER TEMPLATE ==============
const generateWncHeader = (styles, logoUrl) => {
  return `
<!-- WNC Premium Email Header -->
<table width="100%" cellpadding="0" cellspacing="0" border="0" style="background: linear-gradient(135deg, ${styles.headerBgColor} 0%, #001f3f 50%, #001a2e 100%);">
  <tr>
    <td align="center" style="padding: 24px 16px;">
      <table width="100%" cellpadding="0" cellspacing="0" border="0" style="max-width: 600px;">
        <tr>
          <td style="border-top: 2px solid ${styles.headerAccentColor}40; padding-top: 16px;">
            ${styles.showLogo ? `
            <table width="100%" cellpadding="0" cellspacing="0" border="0" style="margin-bottom: 12px;">
              <tr>
                <td align="center">
                  <img src="${logoUrl}" alt="World Nuclear Congress" width="138" height="138" style="display: block; max-width: 138px; height: auto;" />
                </td>
              </tr>
            </table>
            ` : ''}
            <p style="margin: 0 0 12px 0; font-family: Arial, sans-serif; font-size: 11px; font-weight: 600; color: ${styles.headerAccentColor}; text-transform: uppercase; letter-spacing: 2px; text-align: center; word-wrap: break-word;">
              ${styles.headerOverline}
            </p>
            ${styles.headerTitle ? `
            <h1 style="margin: 0 0 8px 0; font-family: Georgia, 'Times New Roman', serif; font-size: 28px; font-weight: 600; color: ${styles.headerTextColor}; text-align: center; line-height: 1.3; word-wrap: break-word;">
              ${styles.headerTitle}
            </h1>
            ` : ''}
            ${styles.headerSubtitle ? `
            <p style="margin: 0 0 16px 0; font-family: Arial, sans-serif; font-size: 14px; color: #cbd5e1; text-align: center; font-weight: 300; line-height: 1.5; word-wrap: break-word;">
              ${styles.headerSubtitle}
            </p>
            ` : ''}
            <table width="60%" cellpadding="0" cellspacing="0" border="0" align="center" style="margin: 0 auto 16px auto; max-width: 200px;">
              <tr>
                <td style="height: 1px; background: linear-gradient(to right, transparent, ${styles.headerAccentColor}80, transparent);"></td>
                <td width="10" style="text-align: center;">
                  <div style="width: 8px; height: 8px; background: ${styles.headerAccentColor}; transform: rotate(45deg); display: inline-block;"></div>
                </td>
                <td style="height: 1px; background: linear-gradient(to left, transparent, ${styles.headerAccentColor}80, transparent);"></td>
              </tr>
            </table>
          </td>
        </tr>
      </table>
    </td>
  </tr>
</table>
<!-- End WNC Premium Email Header -->
`.trim();
};

const EmailReview = () => {
  const navigate = useNavigate();
  const { campaignId } = useParams();
  const token = localStorage.getItem('admin_token');

  const [campaign, setCampaign] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [suppressionInfo, setSuppressionInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [reviewed, setReviewed] = useState(false);
  const [confirmPhrase, setConfirmPhrase] = useState('');
  const [previewDevice, setPreviewDevice] = useState('desktop');

  useEffect(() => {
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchData();
  }, [campaignId, token, navigate]);

  const fetchData = async () => {
    try {
      const [campaignRes, recipientsRes, suppressionRes] = await Promise.all([
        fetch(`${API_URL}/api/email/campaigns/${campaignId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/email/campaigns/${campaignId}/recipients`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/email/campaigns/${campaignId}/suppression-check`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      if (!campaignRes.ok) throw new Error('Campaign not found');
      
      const campaignData = await campaignRes.json();
      const recipientsData = await recipientsRes.json();
      
      setCampaign(campaignData);
      setRecipients(recipientsData);
      
      if (suppressionRes.ok) {
        setSuppressionInfo(await suppressionRes.json());
      }
    } catch (error) {
      toast.error('Failed to load campaign');
      navigate('/admin/email');
    } finally {
      setLoading(false);
    }
  };

  const sendCampaign = async () => {
    if (!reviewed) {
      toast.error('Please confirm that you have reviewed the content and recipients');
      return;
    }
    
    if (confirmPhrase !== 'SEND') {
      toast.error('Please type SEND to confirm');
      return;
    }
    
    try {
      setSending(true);
      
      // Check if campaign uses batch delivery mode
      const deliveryMode = campaign?.delivery_mode || 'immediate';
      const endpoint = deliveryMode !== 'immediate' 
        ? `${API_URL}/api/email/campaigns/${campaignId}/start-batch`
        : `${API_URL}/api/email/campaigns/${campaignId}/send`;
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          reviewed: true,
          confirmation_phrase: 'SEND'
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to send campaign');
      }
      
      const result = await response.json();
      
      // Show different message for batch sends
      if (deliveryMode !== 'immediate') {
        toast.success(`Batch sending started! ${result.queued_count} recipients in ${result.total_batches} batches.`);
        if (result.warnings?.length > 0) {
          result.warnings.forEach(w => toast.warning(w));
        }
      } else {
        toast.success(result.message);
      }
      
      navigate('/admin/email');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSending(false);
    }
  };

  const buildPreviewHtml = () => {
    if (!campaign) return '';
    
    let html = '';
    
    // Get email styles from campaign or use defaults
    const emailStyles = campaign.email_styles ? { ...defaultEmailStyles, ...campaign.email_styles } : defaultEmailStyles;
    
    // Logo URL for header
    const logoUrl = `${API_URL}/assets/images/wnc-logo.png`;
    
    // Add WNC header if enabled
    if (emailStyles.useWncHeader) {
      html += generateWncHeader(emailStyles, logoUrl);
    }
    
    // Add custom header image if set (when WNC header is disabled)
    if (!emailStyles.useWncHeader && campaign.header_image_url) {
      html += `<img src="${campaign.header_image_url}" alt="Header" style="width: 100%; max-width: 600px; height: auto; display: block; margin: 0 auto;" />`;
    }
    
    // Add body wrapper with styles and proper margins (responsive)
    html += `
<table width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color: ${emailStyles.bodyBgColor};">
  <tr>
    <td align="center" style="padding: 24px 16px;">
      <table width="100%" cellpadding="0" cellspacing="0" border="0" style="max-width: 600px;">
        <tr>
          <td style="padding: 20px 24px; font-family: ${emailStyles.bodyFontFamily}; font-size: ${emailStyles.bodyFontSize}; line-height: ${emailStyles.bodyLineHeight}; color: ${emailStyles.bodyTextColor}; word-wrap: break-word;">
            ${campaign.html_body || '<p style="color: #94a3b8; font-style: italic;">Your email content here...</p>'}
          </td>
        </tr>
      </table>
    </td>
  </tr>
</table>`;
    
    // Add footer
    if (campaign.footer_html) {
      html += campaign.footer_html.replace('{{unsubscribe_url}}', '#unsubscribe');
    }
    
    return html;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-[#00457f] border-t-transparent rounded-full" />
      </div>
    );
  }

  const validRecipients = recipients.filter(r => r.is_valid);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-[#00457f] text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(`/admin/email/edit/${campaignId}`)}
              className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
              Back to Editor
            </button>
            <h1 className="text-xl font-bold">Review & Send</h1>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        {/* Warning Banner */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 flex items-start gap-3">
          <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-amber-800">Final Review Required</h3>
            <p className="text-amber-700 text-sm mt-1">
              Please carefully review the email content and recipient list before sending.
              Once sent, emails cannot be recalled.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left - Summary & Confirmation */}
          <div className="space-y-6">
            {/* Campaign Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  Campaign Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Campaign Name</p>
                    <p className="font-medium">{campaign.name}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Status</p>
                    <p className="font-medium capitalize">{campaign.status}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-gray-500">Subject</p>
                    <p className="font-medium">{campaign.subject}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">From</p>
                    <p className="font-medium">{campaign.from_name}</p>
                    <p className="text-xs text-gray-400">{campaign.from_email}</p>
                  </div>
                  {campaign.reply_to && (
                    <div>
                      <p className="text-gray-500">Reply-To</p>
                      <p className="font-medium">{campaign.reply_to}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Delivery Settings Summary */}
            {campaign.delivery_mode && campaign.delivery_mode !== 'immediate' && (
              <Card className="border-purple-200 bg-purple-50/50">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Timer className="w-5 h-5 text-purple-600" />
                    Throttled Delivery Settings
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Delivery Mode</p>
                      <p className="font-medium capitalize flex items-center gap-1">
                        {campaign.delivery_mode === 'throttled' ? (
                          <><Timer className="w-4 h-4" /> Throttled</>
                        ) : (
                          <><Clock className="w-4 h-4" /> Scheduled + Throttled</>
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Batch Size</p>
                      <p className="font-medium">{campaign.batch_size || 50} emails per batch</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Interval</p>
                      <p className="font-medium">Every {campaign.interval_minutes || 30} minutes</p>
                    </div>
                    {campaign.start_at && (
                      <div>
                        <p className="text-gray-500">Starts At</p>
                        <p className="font-medium">{new Date(campaign.start_at).toLocaleString()}</p>
                      </div>
                    )}
                    {campaign.sending_window_start && campaign.sending_window_end && (
                      <div className="col-span-2">
                        <p className="text-gray-500">Daily Window</p>
                        <p className="font-medium">{campaign.sending_window_start} - {campaign.sending_window_end}</p>
                      </div>
                    )}
                    {campaign.max_per_day && (
                      <div>
                        <p className="text-gray-500">Max Per Day</p>
                        <p className="font-medium">{campaign.max_per_day} emails</p>
                      </div>
                    )}
                  </div>
                  <div className="bg-purple-100 rounded-lg p-3 text-sm text-purple-800">
                    <p className="font-medium mb-1">Estimated Delivery</p>
                    <p>
                      {Math.ceil((validRecipients.length / (campaign.batch_size || 50)))} batches will be processed 
                      over approximately {Math.ceil((validRecipients.length / (campaign.batch_size || 50)) * (campaign.interval_minutes || 30))} minutes
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Recipients Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Recipients
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="bg-blue-50 rounded-lg p-4 mb-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-3xl font-bold text-blue-700">
                        {suppressionInfo ? suppressionInfo.will_send_count : validRecipients.length}
                      </p>
                      <p className="text-blue-600 text-sm">Will receive email</p>
                    </div>
                    <Users className="w-12 h-12 text-blue-300" />
                  </div>
                </div>
                
                {/* Suppression Warning */}
                {suppressionInfo && suppressionInfo.suppressed_count > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-red-800 font-medium text-sm">
                          {suppressionInfo.suppressed_count} recipient{suppressionInfo.suppressed_count > 1 ? 's' : ''} will be skipped
                        </p>
                        <p className="text-red-700 text-xs mt-1">
                          These emails are on the suppression list and will not receive this campaign.
                        </p>
                        {Object.keys(suppressionInfo.suppressed_by_reason).length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {Object.entries(suppressionInfo.suppressed_by_reason).map(([reason, count]) => (
                              <span key={reason} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-red-100 text-red-700 capitalize">
                                {reason}: {count}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-amber-800 font-medium text-sm">
                    ⚠️ This will send to {suppressionInfo ? suppressionInfo.will_send_count : validRecipients.length} recipients (1 at a time)
                  </p>
                  <p className="text-amber-700 text-xs mt-1">
                    Each recipient receives their own individual email. No CC/BCC. Full privacy.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Confirmation */}
            <Card className="border-2 border-amber-300">
              <CardHeader className="bg-amber-50">
                <CardTitle className="text-base text-amber-800">
                  Confirmation Required
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-6">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="reviewed"
                    checked={reviewed}
                    onCheckedChange={setReviewed}
                    className="mt-1"
                  />
                  <label htmlFor="reviewed" className="text-sm cursor-pointer">
                    I have reviewed the email content, subject line, sender information, 
                    and recipient list. I confirm this email is ready to send.
                  </label>
                </div>

                <div>
                  <label className="text-sm font-medium block mb-2">
                    Type <span className="font-bold text-red-600">SEND</span> to confirm:
                  </label>
                  <Input
                    value={confirmPhrase}
                    onChange={(e) => setConfirmPhrase(e.target.value.toUpperCase())}
                    placeholder="Type SEND"
                    className="max-w-[200px]"
                  />
                </div>

                <Button
                  onClick={sendCampaign}
                  disabled={sending || !reviewed || confirmPhrase !== 'SEND'}
                  className="w-full bg-green-600 hover:bg-green-700 text-lg py-6"
                >
                  {sending ? (
                    <>
                      <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full mr-2" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Send Campaign to {validRecipients.length} Recipients
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Right - Preview */}
          <div>
            <Card className="sticky top-6">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Eye className="w-5 h-5" />
                    Email Preview
                  </CardTitle>
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant={previewDevice === 'desktop' ? 'default' : 'outline'}
                      onClick={() => setPreviewDevice('desktop')}
                    >
                      <Monitor className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant={previewDevice === 'mobile' ? 'default' : 'outline'}
                      onClick={() => setPreviewDevice('mobile')}
                    >
                      <Smartphone className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="bg-gray-100 rounded-lg p-4 overflow-hidden">
                  {/* Email Header */}
                  <div className="bg-white rounded-t-lg p-3 border-b">
                    <p className="text-xs text-gray-500">
                      From: {campaign.from_name} &lt;{campaign.from_email}&gt;
                    </p>
                    <p className="font-semibold text-sm mt-1">{campaign.subject}</p>
                    {campaign.preheader && (
                      <p className="text-xs text-gray-400 truncate">{campaign.preheader}</p>
                    )}
                  </div>
                  
                  {/* Email Body */}
                  <div
                    className={`bg-white rounded-b-lg overflow-auto transition-all ${
                      previewDevice === 'mobile' ? 'max-w-[320px] mx-auto' : 'w-full'
                    }`}
                    style={{ maxHeight: '500px' }}
                  >
                    <div
                      className="p-4"
                      dangerouslySetInnerHTML={{ __html: buildPreviewHtml() }}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default EmailReview;
